// PROMO / CUCI GUDANG — helper murni kampanye diskon berperiode (mig 3470).
//
// Kampanye = aturan potongan yang menyasar produk / kategori / merek / semua,
// berlaku dalam satu rentang waktu. Skemanya cermin Program Poin (mig 139/140);
// yang berbeda hanya akibatnya: program poin memberi poin, promo mengubah HARGA.
//
// ────────────────────────────────────────────────────────────────────────────
// KEPUTUSAN PEMILIK — ditulis di sini supaya alasannya tidak hanya hidup di
// riwayat chat dan "diperbaiki" orang lain enam bulan lagi:
//
//   1. PROMO MENANG MUTLAK — untuk kampanye ber-`level_policy = 'replace'`
//      (bawaan). Saat promo berlaku, harganya dipakai untuk SEMUA orang termasuk
//      reseller — diskon level DIABAIKAN (tidak bertumpuk, bukan min()). Lihat
//      cabang promo di pricing.ts::explainVariantPrice. Konsekuensi yang
//      diterima sadar: reseller yang harga levelnya lebih murah justru membayar
//      lebih mahal selama promo; layar kampanye menandai kasus itu SEBELUM admin
//      menekan Simpan.
//      1b. DISKON HARGA CORET — `level_policy = 'stack'` (keputusan pemilik
//      2026-09-16, docs/PUTUSAN-PROMO.md). Potongan kampanye dipotong dari harga
//      jual umum lebih dulu ("harga coret" baru), LALU diskon level (override
//      per-varian / diskon grup) dihitung dari harga coret itu — berantai:
//      100.000 − 10 % = 90.000, lalu Distributor 30 % → 63.000. Badge pembeli
//      ber-level menyebut keduanya ("🔥 10% + 30%"); pembeli tanpa level melihat
//      persis seperti kampanye biasa. Angka yang dicoret di layar = harga
//      sebelum potongan TERAKHIR (90.000 untuk reseller, 100.000 untuk tamu).
//   2. ONLINE SAJA — KASIR (POS) TIDAK IKUT. pos.ts sengaja tidak memanggil
//      apa pun dari modul ini; menurunkan harga di kasir sudah bisa lewat
//      override harga per-baris & diskon transaksi (mig 147). Dikunci tes di
//      pos.test.ts.
//   3. SELALU YANG TERMURAH bila beberapa kampanye kena satu produk — tidak ada
//      kolom prioritas. Lihat pickCheapestPromo.
//   4. PERIODE DIEVALUASI SAAT BACA (isPromoRunning), tanpa cron.
//   5. HARGA TERKUNCI SAAT PESAN: promo masuk ke order_items.unit_price di
//      checkout, jadi nota mengikutinya walau promo sudah berakhir.
//
// Seluruh matematika harga tetap di TS — SQL tak pernah menghitung diskon
// (aturan repo, backfillUnitPrice.ts:5-9).

import { MAX_GROUP_SLOT } from "@/lib/reseller-groups";
import { normalizeSizeLabel } from "@/lib/sizes";

export type PromoDiscountMode = "persen" | "nominal";
/**
 * Cara kampanye bergabung dengan diskon level (`promo_campaigns.level_policy`):
 *   • `replace` — promo menang mutlak (keputusan #1; bawaan & seluruh kampanye lama).
 *   • `stack`   — "Diskon harga coret": potongan promo dulu, lalu diskon level
 *                 dihitung dari harga coret (keputusan #1b).
 * Nilai lain dari DB dibaca sebagai `replace` supaya kampanye tak pernah diam-diam
 * berubah cara berlakunya karena klien tak mengenal nilainya.
 */
export type PromoLevelPolicy = "replace" | "stack";
export type PromoTargetKind = "product" | "category" | "brand";
export type PromoTargetType = PromoTargetKind | "all";
export type PromoProductTypeScope = "preorder" | "ready_stock" | "all";

/**
 * Peran satu baris target (mig 6010, usulan 1.2): `include` = target inklusif
 * (bawaan; baris lama tanpa kolom = include), `exclude` = pengecualian
 * "semua KECUALI …" — satu yang cocok membatalkan cakupan, apa pun `targetType`.
 */
export type PromoTargetMode = "include" | "exclude";

export type PromoTarget = {
  kind: PromoTargetKind;
  productId: string | null;
  /** Snapshot nama produk/kategori/merek (kategori & merek dicocokkan by nama). */
  name: string;
  /** Absen = `include` (fixture & baris pra-6010). */
  mode?: PromoTargetMode;
};

/**
 * Potongan berbeda untuk satu label UKURAN (mig 3670) — "sisa 2XL–4XL 50%,
 * ukuran laris 30%". Sumbu ukuran dipilih lebih dulu daripada sumbu lain
 * karena ia satu-satunya yang sudah TERNORMALISASI lintas produk
 * (`normalize_size_label`, mig 197) sehingga sebuah kampanye bertarget merek
 * bisa menyebut "2XL" sekali dan mengenai puluhan produk berstruktur berbeda.
 *
 * `size` SELALU bentuk ternormalisasi (hasil `normalizeSizeLabel`) — di DB
 * dijaga trigger, di TS dijaga editor. Kalau tidak, "XXL" dan "2XL" jadi dua
 * aturan yang berebut satu varian.
 */
export type PromoSizeRule = {
  size: string;
  mode: PromoDiscountMode;
  value: number;
};

export type PromoCampaign = {
  id: string;
  /**
   * Toko pemilik kampanye. Dibawa sampai ke klien SEBAGAI LAPIS KEDUA: query
   * sudah memfilter store_id (dan fail-closed bila toko tak ter-resolusi), tapi
   * target kategori/merek dicocokkan BY NAMA — satu kekeliruan pengiriman akan
   * mendiskon produk toko lain tanpa error apa pun. Dengan kolom ini, penerima
   * masih bisa menolak kampanye asing alih-alih mempercaya pemanggilnya.
   */
  storeId: string | null;
  name: string;
  description: string | null;
  isActive: boolean;
  startsAt: string | null;
  endsAt: string | null;
  discountMode: PromoDiscountMode;
  discountValue: number;
  productTypeScope: PromoProductTypeScope;
  targetType: PromoTargetType;
  targets: PromoTarget[];
  /** Potongan khusus per ukuran; kosong = seluruh ukuran ikut potongan utama. */
  sizeRules: PromoSizeRule[];
  /**
   * true = ukuran yang TIDAK disebut di `sizeRules` tak dapat potongan sama
   * sekali ("hanya 2XL–4XL yang dicuci gudang"). false (bawaan) = mereka ikut
   * potongan utama kampanye. Sengaja satu saklar, bukan aturan bernilai 0 untuk
   * tiap ukuran sisa — toko dengan 20 ukuran akan menuntut 19 baris isian untuk
   * menyatakan satu niat sederhana.
   */
  sizeRulesExclusive: boolean;
  /**
   * Slot level reseller yang berhak (mig 3680). KOSONG = seluruh pembeli,
   * termasuk tamu — perilaku sebelum mig 3680, dan itu bawaannya.
   *
   * Slot, bukan id grup: `int[]` mustahil menunjuk baris toko lain, sedangkan
   * array id menuntut validasi lintas-toko sendiri (mig 3550 = riwayat panjang
   * kebocoran lewat id & nama). Harga yang dibayar: slot adalah POSISI, bukan
   * identitas — memakai ulang slot untuk level berbeda memindahkan cakupan
   * kampanye lama ke penghuni barunya. Editor menampilkan nama penghuni tiap
   * slot supaya pilihannya tak pernah buta.
   */
  levelSlots: number[];
  /** Cara bergabung dengan diskon level — lihat `PromoLevelPolicy`. */
  levelPolicy: PromoLevelPolicy;
  /**
   * Ikut dipakai Kasir/POS (mig 6010, usulan 1.3). Bawaan false = keputusan #2
   * mig 3470 (kasir harga normal/level) tetap berlaku; `pos.ts` hanya
   * meneruskan kampanye yang menyalakannya.
   */
  applyToPos: boolean;
  /**
   * Kampanye `replace` tak boleh membuat pembeli ber-level membayar LEBIH
   * MAHAL dari harga levelnya (mig 6010, usulan 1.4): harga = min(promo,
   * level); yang menang menentukan badge & jejak. Tak bermakna pada `stack`
   * (level sudah dipotong di atas harga coret). Bawaan false = perilaku lama.
   */
  neverWorse: boolean;
  /**
   * Plafon pcs seluruh pembeli digabung (mig 6020, usulan 3.1). NULL = TAK
   * TERBATAS — bukan 0. Ditegakkan trigger `enforce_promo_quota` saat memesan;
   * angka di klien hanya untuk tampilan & pencegahan dini.
   */
  quotaTotal: number | null;
  /** Plafon pcs per pembeli (mig 6020). NULL = tak dibatasi. */
  perCustomerLimit: number | null;
};

/**
 * Promo yang RELEVAN untuk satu produk — sudah lolos target + tipe + periode.
 * Bentuk ringkas ini yang dioper ke `pricing.ts`; kampanye penuhnya tak perlu.
 */
export type ProductPromo = {
  campaignId: string;
  name: string;
  mode: PromoDiscountMode;
  value: number;
  /** Dipakai badge/keterangan storefront. */
  endsAt: string | null;
  /** Potongan khusus per ukuran (mig 3670); kosong = semua ikut `mode`/`value`. */
  sizeRules?: readonly PromoSizeRule[];
  /** Ukuran tanpa aturan tidak dapat potongan sama sekali. */
  sizeRulesExclusive?: boolean;
  /** Slot level yang berhak (mig 3680); kosong/undefined = semua pembeli. */
  levelSlots?: readonly number[];
  /**
   * Cara bergabung dengan diskon level. OPSIONAL: absen = `replace` supaya
   * pemanggil & fixture lama tetap memakai aturan "promo menang mutlak".
   */
  levelPolicy?: PromoLevelPolicy;
  /**
   * `never_worse` (mig 6010) — hanya bermakna bila `levelPolicy` = replace.
   * OPSIONAL: absen = false, pemanggil & fixture lama tak berubah.
   */
  neverWorse?: boolean;
};

/**
 * SATU potongan yang benar-benar membentuk harga — bahan badge, jejak
 * `order_items.*_discount_*`, dan tier voucher. `source` menyebut asalnya:
 *   • `promo`    — kampanye (mode & nilai aturan yang berlaku untuk ukuran itu),
 *   • `override` — diskon khusus varian untuk slot grup pembeli,
 *   • `group`    — diskon grup rata (`reseller_groups.discount_persen`),
 *   • `group_brand` — diskon merek per level (mig 7070), menggantikan dua
 *                  di atas untuk merek yang punya aturan.
 * Didefinisikan di sini (bukan pricing.ts) supaya pembuat teks badge di bawah
 * bisa dipakai tanpa menyeret pricing.ts — pricing.ts mengimpor berkas ini.
 */
export type PriceCut = {
  source: "promo" | "override" | "group" | "group_brand";
  mode: PromoDiscountMode;
  value: number;
};

/**
 * Potongan LEVEL (bukan promo) — satu penentu untuk jejak `level_discount_*`,
 * pemisah promo/level di nota, dan tier voucher. Exhaustive (`never`): sumber
 * potongan baru WAJIB memilih sisinya di sini, bukan diam-diam hilang dari
 * jejak seperti yang terjadi bila pemanggil memeriksa `override || group`.
 */
export function isLevelCut(cut: Pick<PriceCut, "source">): boolean {
  switch (cut.source) {
    case "promo":
      return false;
    case "override":
    case "group":
    case "group_brand":
      return true;
    default: {
      const never: never = cut.source;
      return never;
    }
  }
}

/** `replace` bila kampanye tak menyebut kebijakan (kampanye/fixture lama). */
export function promoLevelPolicy(
  promo: Pick<ProductPromo, "levelPolicy"> | null | undefined
): PromoLevelPolicy {
  return promo?.levelPolicy === "stack" ? "stack" : "replace";
}

/**
 * Acuan produk seminimal mungkin — SENGAJA bukan `Product` penuh, supaya
 * pemanggil yang hanya memegang beberapa kolom (resolver checkout, kartu
 * ringkas) tak perlu memuat produk lengkap.
 */
export type PromoProductRef = {
  id: string;
  brand?: string | null;
  category?: string | null;
  productType?: "preorder" | "ready_stock" | null;
};

export type PromoStatus = "Berjalan" | "Terjadwal" | "Berakhir" | "Nonaktif";

/** Samakan nama untuk pencocokan by-nama: buang spasi tepi + lowercase. */
/**
 * Normalisasi nama merek/kategori untuk pencocokan target — cermin SQL
 * `lower(btrim())` (promo_campaign_covers_product, mig 6000). Diekspor untuk
 * diskon merek per level (`groupBrandDiscount.ts`, mig 7070) supaya kedua
 * pencocok merek memakai SATU aturan.
 */
export function normName(raw: string | null | undefined): string {
  return (raw ?? "").trim().toLowerCase();
}

/** `null` bila tanggal kosong/tak terbaca — perlakuannya = "tanpa batas". */
function parseTime(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : null;
}

/** Angka defensif dari PostgREST (numeric bisa datang sebagai string). */
function num(raw: unknown, fallback = 0): number {
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : fallback;
  if (typeof raw === "string") {
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

/** Kampanye sedang berlaku: aktif DAN `now` berada di dalam periodenya. */
export function isPromoRunning(campaign: PromoCampaign, now: Date): boolean {
  if (!campaign.isActive) return false;
  const t = now.getTime();
  if (!Number.isFinite(t)) return false;

  const start = parseTime(campaign.startsAt);
  const end = parseTime(campaign.endsAt);
  if (start !== null && t < start) return false;
  if (end !== null && t > end) return false;
  return true;
}

/** Badge status untuk daftar admin. */
export function promoStatusLabel(campaign: PromoCampaign, now: Date): PromoStatus {
  if (!campaign.isActive) return "Nonaktif";
  const t = now.getTime();
  const start = parseTime(campaign.startsAt);
  const end = parseTime(campaign.endsAt);
  if (start !== null && t < start) return "Terjadwal";
  if (end !== null && t > end) return "Berakhir";
  return "Berjalan";
}

/** Satu baris target cocok dengan produk (produk by id; merek & kategori BY NAMA). */
function targetMatches(
  target: PromoTarget,
  product: PromoProductRef,
  brand: string,
  category: string
): boolean {
  if (target.kind === "product") return Boolean(target.productId) && target.productId === product.id;
  const name = normName(target.name);
  if (!name) return false;
  if (target.kind === "brand") return name === brand;
  return name === category;
}

/**
 * Apakah kampanye mencakup produk ini (target + tipe produk), TANPA cek periode.
 *
 * Sejak mig 6010 (usulan 1.2): kena bila lolos target INKLUSIF (`targetType`
 * all, atau satu baris `include` sejenis cocok) DAN tak kena satu pun baris
 * `exclude` — pengecualian dinilai apa pun jenisnya ("semua KECUALI produk X",
 * "merek Y kecuali kategori Z"). CERMIN `promo_campaign_covers_product` (SQL)
 * dan salinannya `promo_campaign_covers_product_for_slot` (mig 7080, + level
 * pembeli — yang dipakai gerbang lantai harga) — kalau satu berubah, ketiganya.
 * Tripwire: `promoFloorGateParity.test.ts`.
 */
export function campaignCoversProduct(
  campaign: Pick<PromoCampaign, "productTypeScope" | "targetType" | "targets">,
  product: PromoProductRef
): boolean {
  // Tipe produk dulu — paling murah & paling sering menyaring.
  if (campaign.productTypeScope !== "all") {
    const type = product.productType ?? "preorder";
    if (type !== campaign.productTypeScope) return false;
  }

  const brand = normName(product.brand);
  const category = normName(product.category);

  for (const target of campaign.targets) {
    if (target.mode === "exclude" && targetMatches(target, product, brand, category)) return false;
  }

  if (campaign.targetType === "all") return true;

  for (const target of campaign.targets) {
    if (target.mode === "exclude") continue;
    if (target.kind !== campaign.targetType) continue;
    if (targetMatches(target, product, brand, category)) return true;
  }

  return false;
}

/**
 * Sisa kuota per kampanye (mig 6020) — `campaignId` → pcs tersisa.
 *
 * Nilai `null` atau kunci yang TIDAK ADA berarti "tak dibatasi / belum
 * terbaca", dan kampanyenya tetap ditawarkan. HANYA angka 0 (atau negatif)
 * yang membuang kampanye. Peta ini sengaja tak wajib: pemuatannya bisa gagal,
 * dan menutup promo karena satu fetch meleset akan MENAIKKAN harga yang sudah
 * dilihat pembeli — penegakan sebenarnya ada di trigger DB, ini hanya
 * mencegah menawarkan yang pasti sudah habis.
 */
export type PromoRemainingMap = ReadonlyMap<string, number | null>;

/** Kampanye masih punya sisa kuota menurut peta (absen/null = ya). */
export function promoHasRemaining(
  campaignId: string,
  remaining: PromoRemainingMap | null | undefined
): boolean {
  if (!remaining) return true;
  const left = remaining.get(campaignId);
  if (left === null || left === undefined) return true;
  return left > 0;
}

/**
 * Sisa kuota kampanye UNTUK SATU PEMBELI dari baris `promo_campaign_my_quota`
 * (mig 6020) — bughunt D16-11. Jatah pribadi ATAU kuota total yang tandas = 0;
 * selebihnya angka terkecil yang diketahui; keduanya tak berkuota (NULL) =
 * `null` (tak terbatas). Baris yang tak terbaca = `undefined` (tak diketahui) —
 * pemanggil TIDAK memasukkannya ke peta, supaya promo tetap ditawarkan.
 */
export function promoQuotaLeftForMe(
  row: { remaining_total: number | null; remaining_for_me: number | null } | null | undefined
): number | null | undefined {
  if (!row) return undefined;
  const known = [row.remaining_total, row.remaining_for_me].filter(
    (n): n is number => typeof n === "number" && Number.isFinite(n)
  );
  if (!known.length) return null;
  return Math.max(0, Math.min(...known));
}

/**
 * Promo yang kuotanya HABIS bagi pembeli ini dibuang dari daftar CHECKOUT
 * (bughunt D16-11) — cermin `promosForProduct({ remaining })` di storefront.
 * Tanpa ini resolver checkout tetap memilih kampanye yang habis, baris
 * berharga promo, lalu gerbang kuota menolak SELURUH checkout produknya padahal
 * layar sudah memajang harga normal. Absen di peta = tak diketahui = tetap.
 */
export function dropExhaustedPromos(
  promos: readonly ProductPromo[],
  remaining: PromoRemainingMap | null | undefined
): ProductPromo[] {
  return promos.filter((p) => promoHasRemaining(p.campaignId, remaining));
}

/**
 * Chip "Sisa N pcs" untuk kampanye berkuota (mig 6020). `null` = tak berkuota,
 * belum terbaca, atau sudah habis — permukaan tak menampilkan apa pun.
 *
 * Kampanye HABIS sengaja memulangkan `null`, bukan "Sisa 0 pcs": begitu kuota
 * tandas, `promosForProduct` berhenti menawarkannya dan harga kembali normal,
 * jadi chip "0" akan menempel pada harga yang tak lagi berpromo.
 */
export function promoRemainingLabel(
  campaignId: string,
  remaining: PromoRemainingMap | null | undefined
): string | null {
  if (!remaining) return null;
  const left = remaining.get(campaignId);
  if (typeof left !== "number" || left <= 0) return null;
  return `Sisa ${left} pcs`;
}

/**
 * Promo yang berlaku untuk satu produk saat `now`. Biasanya 0 atau 1 anggota;
 * lebih dari satu diselesaikan `pickCheapestPromo` pada harga varian.
 * Cermin `programsForProduct` di loyalty.ts.
 *
 * `opts.remaining` (mig 6020): kampanye yang kuotanya HABIS tak ikut bersaing —
 * pembeli melihat harga normal/level, bukan harga promo yang akan ditolak
 * trigger saat checkout.
 */
export function promosForProduct(
  campaigns: readonly PromoCampaign[],
  product: PromoProductRef,
  now: Date,
  opts?: { remaining?: PromoRemainingMap | null }
): ProductPromo[] {
  const out: ProductPromo[] = [];
  for (const campaign of campaigns) {
    if (!isPromoRunning(campaign, now)) continue;
    if (!campaignCoversProduct(campaign, product)) continue;
    if (!promoHasRemaining(campaign.id, opts?.remaining)) continue;
    out.push({
      campaignId: campaign.id,
      name: campaign.name,
      mode: campaign.discountMode,
      value: campaign.discountValue,
      endsAt: campaign.endsAt,
      sizeRules: campaign.sizeRules,
      sizeRulesExclusive: campaign.sizeRulesExclusive,
      levelSlots: campaign.levelSlots,
      levelPolicy: campaign.levelPolicy,
      neverWorse: campaign.neverWorse
    });
  }
  return out;
}

/**
 * Kampanye milik TOKO INI saja — lapis kedua di sisi ADMIN (audit A6, 2026-09-16).
 *
 * Fetcher admin (`fetchActivePromoCampaignsAdmin`) mengandalkan RLS
 * `is_admin() and (current_store_id() is null or store_id = current_store_id())`.
 * `current_store_id()` (mig 3020) NULL untuk pemilik platform yang profilnya
 * tak terpaut toko — RLS lalu memulangkan kampanye SEMUA toko, dan
 * `promosForProduct` mencocokkan kategori/merek BY NAMA, sehingga "Gamis −50 %"
 * milik toko lain ter-prefill ke nota / keranjang admin toko ini tanpa error.
 * Storefront sudah fail-closed lewat filter `store_id` di query; sisi admin
 * belum punya lapis itu — inilah lapisnya.
 *
 * `storeId` null/undefined (toko tak ter-resolusi) = HASIL KOSONG, bukan
 * "pakai semua" (aturan turunan A2 di CLAUDE.md). Kampanye ber-`storeId` null
 * (baris tanpa kolom `store_id` di select lama) ikut dibuang — lebih baik
 * promo tak ter-prefill daripada promo asing.
 */
/** Klem interval pemuatan ulang kampanye di klien (ms): 15 dtk … 5 mnt. */
export const PROMO_REFETCH_MIN_MS = 15_000;
export const PROMO_REFETCH_MAX_MS = 300_000;

/**
 * Jarak (ms) ke batas kampanye TERDEKAT di masa depan — `starts_at` yang belum
 * tiba atau `ends_at` yang belum lewat — di antara kampanye yang dimuat;
 * `null` bila tak ada batas di depan. Dipakai `PromoContext` untuk memuat
 * ulang & memutar jam TEPAT di batas itu (audit A10: katalog pernah
 * tertinggal ≤ 2 menit dari akhir kampanye — harga promo masih tampil sesudah
 * kampanye usai, sementara checkout sudah menagih harga normal).
 */
export function promoNextBoundaryMs(
  campaigns: ReadonlyArray<Pick<PromoCampaign, "startsAt" | "endsAt">>,
  now: Date | null
): number | null {
  if (!now) return null;
  const t0 = now.getTime();
  let next: number | null = null;
  for (const c of campaigns) {
    for (const iso of [c.startsAt, c.endsAt]) {
      if (!iso) continue;
      const t = new Date(iso).getTime();
      if (!Number.isFinite(t)) continue;
      const d = t - t0;
      if (d > 0 && (next === null || d < next)) next = d;
    }
  }
  return next;
}

/**
 * Interval `refetchInterval` TanStack untuk daftar kampanye: sedikit LEWAT
 * batas terdekat (supaya server sudah berdiri di sisi baru), diklem
 * 15 dtk–5 mnt; tanpa batas di depan → plafon.
 */
export function promoRefetchIntervalMs(
  campaigns: ReadonlyArray<Pick<PromoCampaign, "startsAt" | "endsAt">>,
  now: Date | null
): number {
  const next = promoNextBoundaryMs(campaigns, now);
  if (next === null) return PROMO_REFETCH_MAX_MS;
  return Math.min(PROMO_REFETCH_MAX_MS, Math.max(PROMO_REFETCH_MIN_MS, next + 1_000));
}

/**
 * Tundaan `setTimeout` untuk memutar jam dinding tepat di batas kampanye
 * (`PromoContext`): sedikit LEWAT batas (+500 ms) supaya `runningPromoCampaigns`
 * sudah berdiri di sisi baru, minimal 1 dtk, dan **DIKLEM ke plafon refetch**.
 *
 * Klemnya bukan kosmetik. `setTimeout` menyimpan tundaan sebagai int32 — di
 * atas 2^31−1 ms (≈24,8 hari) browser MENJALANKANNYA SEGERA, bukan menunda.
 * Tanpa klem, kampanye yang batasnya jauh (mis. berakhir 31 Des) membuat
 * callback menembak seketika → `setNow` → efek terpasang ulang → loop render
 * tanpa henti di setiap halaman storefront toko itu (bug live zzhomey,
 * 2026-09-17). `null` bila tak ada batas = jangan pasang timer.
 */
export function promoBoundaryTimerMs(nextBoundary: number | null): number | null {
  if (nextBoundary === null || !Number.isFinite(nextBoundary)) return null;
  return Math.min(PROMO_REFETCH_MAX_MS, Math.max(1_000, nextBoundary + 500));
}

export function promosForStore<T extends Pick<PromoCampaign, "storeId">>(
  campaigns: readonly T[],
  storeId: string | null | undefined
): T[] {
  if (!storeId) return [];
  return campaigns.filter((c) => c.storeId === storeId);
}

/**
 * Harga setelah promo. SALINAN PERILAKU `applyVariantOverride` (pricing.ts:47-53)
 * — disamakan supaya pembulatan promo tak pernah berbeda dari pembulatan diskon
 * varian. `nominal` = potongan Rp PER PCS, satu satuan dengan diskon varian.
 */
export function applyPromo(
  base: number,
  promo: ProductPromo,
  sizeLabel?: string | null
): number {
  const rule = promoDiscountForSize(promo, sizeLabel ?? null);
  // null = kampanye ini memang tak menawarkan apa pun untuk ukuran tsb.
  if (!rule) return Math.max(0, Math.round(base));
  const value = Number.isFinite(rule.value) ? Math.max(0, rule.value) : 0;
  const price = rule.mode === "nominal" ? base - value : base * (1 - value / 100);
  return Math.max(0, Math.round(price));
}

/**
 * Slot "PELANGGAN UMUM" di `promo_campaigns.level_slots` (mig 5950) — pembeli
 * TANPA level: tamu, pelanggan tanpa grup, dan pengunjung katalog reseller
 * `/toko/[kode]` (grup dipaksa null). Bukan slot `reseller_groups` sungguhan
 * (mig 5600: 1..MAX_GROUP_SLOT), melainkan nilai yang boleh DISEBUT di cakupan
 * kampanye supaya admin bisa memilih "pelanggan umum DAN level tertentu" secara
 * eksplisit — cermin idiom voucher `voucher_allows_level` (mig 5610:
 * `coalesce(p_slot, 0) = any(p_slots)`). Kunci 0 di `PROMO_LEVEL_SLOTS` /
 * `promoTotalsByLevel` adalah nilai yang sama.
 */
export const PROMO_GENERAL_SLOT = 0;

/**
 * Berhakkah pembeli ber-level `slot` atas kampanye ini? (mig 3680 → 5950)
 *
 * `slot` null = pembeli TANPA level: tamu, pelanggan biasa, dan pengunjung
 * katalog reseller `/toko/[kode]` yang memang dipaksa tanpa grup — dinilai
 * sebagai `PROMO_GENERAL_SLOT` (0). Mereka berhak atas kampanye umum (cakupan
 * kosong) dan atas kampanye yang MENYEBUT 0; kampanye yang hanya menyebut
 * level 1..8 tetap tak pernah bocor ke katalog reseller, tanpa satu baris pun
 * kode di sana.
 *
 * Cakupan KOSONG = semua berhak. Sengaja begitu, bukan "tak seorang pun":
 * kolomnya lahir kosong untuk seluruh kampanye yang sudah ada, jadi tafsir
 * sebaliknya akan mematikan semua promo yang sedang berjalan pada saat migrasi.
 * Kompatibel mundur: kampanye ber-slot 1..8 (tanpa 0) berperilaku PERSIS
 * seperti sebelum 5950 — pembeli tanpa level tetap tak berhak.
 *
 * Dicermin SQL `promo_campaign_covers_product_for_slot` (mig 7080): gerbang
 * lantai harga hanya menyingkir untuk kampanye yang berhak bagi slot pembeli
 * (`promo_customer_slot` = grup AKTIF, cermin `groupFromRow`). Kalau aturan ini
 * berubah tanpa sisi SQL, harga promo yang sah ditolak "Harga satuan tidak sah".
 */
export function promoAllowsLevel(
  promo: Pick<ProductPromo, "levelSlots">,
  slot: number | null | undefined
): boolean {
  const slots = promo.levelSlots ?? [];
  if (!slots.length) return true;
  return slots.includes(slot ?? PROMO_GENERAL_SLOT);
}

/**
 * Saring daftar promo ke yang BERHAK bagi pembeli ini — SATU-SATUNYA tempat
 * aturan kelayakan level dijalankan.
 *
 * Diekstrak justru karena ada DUA pemakai yang wajib sepakat, dan keduanya
 * gagal dengan cara berbeda kalau dibiarkan punya aturan sendiri:
 *   • `pricing.ts` menentukan ANGKA yang dibayar,
 *   • `usePromosFor` menentukan apa yang dilihat mata — kartu memakai
 *     `promos.length` untuk memasang pil "🔥" dan menyembunyikan badge
 *     "Harga <Grup>".
 * Bila hanya lapisan harga yang menyaring, kartu akan memasang 🔥 pada harga
 * yang sama sekali tidak didiskon: pembeli membaca promo yang tak ada.
 */
export function promosForLevel(
  promos: readonly ProductPromo[],
  slot: number | null | undefined
): ProductPromo[] {
  return promos.filter((p) => promoAllowsLevel(p, slot));
}

/**
 * Promo yang berlaku bagi TAMU (tanpa level) untuk satu produk, sisa kuota
 * ikut — dasar "harga yang dilihat tamu" di feed Katalog Instagram, JSON-LD
 * halaman produk (`productOffers.ts`), dan pixel iklan (B7, PR-IG-4).
 *
 * Dihitung dari daftar kampanye UTUH, bukan dari hasil `usePromosFor`: daftar
 * itu sudah disaring level SESI, jadi bagi reseller kampanye umum yang tak
 * mencakup levelnya sudah hilang — harga tamu yang diturunkan darinya salah.
 */
export function guestPromosFor(
  campaigns: readonly PromoCampaign[],
  product: PromoProductRef,
  now: Date,
  opts?: { remaining?: PromoRemainingMap | null }
): ProductPromo[] {
  return promosForLevel(promosForProduct(campaigns, product, now, opts), null);
}

/**
 * Slot level yang MUNGKIN dimiliki pembeli. `0` = tanpa level — tamu belum
 * login, pelanggan tanpa grup, dan katalog reseller yang memaksa `group=null`.
 * Bukan slot sungguhan (slot DB selalu 1..MAX_GROUP_SLOT), tapi kunci yang
 * stabil untuk peta agregat di bawah; `null` tak bisa jadi kunci objek tanpa
 * jadi string "null".
 *
 * Diturunkan dari `MAX_GROUP_SLOT` (mig 5600), bukan literal: halaman `/promo`
 * dirender SERVER dan memakai peta ini untuk menghitung "N produk sedang promo"
 * per level. Daftar yang tertinggal di 4 membuat pembeli grup 5..8 membaca
 * hitungan yang bukan miliknya.
 */
export const PROMO_LEVEL_SLOTS = Array.from(
  { length: MAX_GROUP_SLOT + 1 },
  (_, i) => i
) as readonly number[];

/**
 * Jumlah produk promo PER KELAYAKAN LEVEL — satu angka untuk tiap slot yang
 * mungkin (termasuk 0 = tanpa level).
 *
 * KENAPA LIMA ANGKA, BUKAN SATU. Halaman `/promo` dirender SERVER, dan server
 * tak pernah tahu level pengunjungnya (level baru diketahui setelah
 * CustomerContext termuat di klien). Sebelumnya server mengirim SATU `totalAll`
 * yang menghitung produk SELURUH kampanye berjalan; tamu karena itu membaca
 * "N produk sedang promo" yang ikut menghitung produk kampanye ber-level yang
 * tak berhak baginya — lengkap dengan kartu skeleton yang tak akan pernah
 * terisi dan tombol "Muat Lebih Banyak" yang tak menghasilkan apa-apa.
 *
 * Menyuruh klien menjumlah `countByCampaign` TIDAK BISA menggantikannya: satu
 * produk boleh kena beberapa kampanye sekaligus, jadi penjumlahan itu
 * menghitung ganda. Yang dibutuhkan adalah UNION per level — dan hanya server
 * yang memegang index katalog penuh untuk menghitungnya. Lima angka murah:
 * slot hanya 1..4 dan sebagian besar toko cuma punya satu-dua kampanye.
 */
export function promoTotalsByLevel<T extends PromoProductRef>(
  products: readonly T[],
  campaigns: readonly PromoCampaign[],
  now: Date
): Record<number, number> {
  const totals: Record<number, number> = {};
  for (const key of PROMO_LEVEL_SLOTS) {
    const slot = key === PROMO_GENERAL_SLOT ? null : key;
    const eligible = campaigns.filter((c) =>
      promoAllowsLevel({ levelSlots: c.levelSlots }, slot)
    );
    totals[key] = eligible.length ? promoProducts(products, eligible, now).length : 0;
  }
  return totals;
}

/**
 * Angka yang benar untuk pembeli ini dari peta `promoTotalsByLevel`.
 *
 * FAIL-CLOSED ke 0 saat petanya hilang/rusak: angka yang terlalu BESAR
 * melahirkan kartu skeleton hantu & tombol "Muat Lebih Banyak" yang tak
 * menghasilkan apa-apa, sedangkan 0 hanya membuat halaman memakai jumlah yang
 * benar-benar terlihat.
 */
export function promoTotalForLevel(
  totals: Record<number, number> | null | undefined,
  slot: number | null | undefined
): number {
  if (!totals) return 0;
  const key = slot === null || slot === undefined ? 0 : slot;
  const n = totals[key];
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/**
 * Potongan yang berlaku untuk satu ukuran — SATU-SATUNYA penjawab "kampanye ini
 * memberi apa untuk varian ukuran X". Dipakai `applyPromo` (harga) DAN editor
 * kampanye (pratinjau), supaya keduanya mustahil menyimpang.
 *
 * `null` berarti **tidak dapat potongan sama sekali**, dan itu SENGAJA
 * dibedakan dari `{value: 0}` — untuk EDITOR (baris "0" = admin sengaja
 * mengeluarkan ukuran itu tanpa menghapus barisnya). Untuk HARGA keduanya kini
 * sama: `promoRuleUsable` membuat aturan bernilai 0 tak ikut bersaing di
 * `pickCheapestPromo`, sehingga varian itu tidak dilabeli promo dan pembeli
 * ber-level tetap mendapat diskon levelnya (audit A3, 2026-09-16). Sebelumnya
 * aturan 0 tetap diklaim: harga dasar PENUH untuk semua orang, termasuk
 * reseller yang seharusnya mendapat 30 %.
 *
 * Pencocokan label EXACT (bukan case-insensitive): kedua sisi sudah lewat
 * `normalizeSizeLabel`, dan melonggarkannya di sini justru menyembunyikan
 * aturan tersimpan yang belum ternormalisasi.
 */
export function promoDiscountForSize(
  promo: ProductPromo,
  sizeLabel: string | null
): { mode: PromoDiscountMode; value: number } | null {
  const rules = promo.sizeRules ?? [];
  if (!rules.length) return { mode: promo.mode, value: promo.value };

  if (sizeLabel) {
    const hit = rules.find((r) => r.size === sizeLabel);
    if (hit) return { mode: hit.mode, value: hit.value };
  }

  // Varian yang ukurannya tak dikenali diperlakukan sama dengan ukuran yang
  // tak disebut: ikut potongan utama, atau tak dapat apa-apa bila eksklusif.
  return promo.sizeRulesExclusive ? null : { mode: promo.mode, value: promo.value };
}

/**
 * Apakah aturan potongan ini BISA DIPAKAI pada harga dasar `base` — audit
 * A2/A3 (2026-09-16). Aturan yang tak bisa dipakai membuat kampanyenya TIDAK
 * mengklaim varian: harga jatuh ke diskon level pembeli, tanpa jejak kampanye.
 *
 *   • `null` (ukuran dikecualikan) — memang tak menawarkan apa pun.
 *   • nilai 0 — "ukuran ini tidak ikut promo". Dulu tetap diklaim: harga = harga
 *     dasar PENUH dan diskon level dilewati ("promo menang mutlak" atas potongan
 *     nol), jadi reseller 30 % membayar 100.000 untuk ukuran yang admin
 *     tandai tak ikut promo. Kini setara dengan dikecualikan.
 *   • nominal > harga dasar — mustahil untuk harga itu. Dulu `applyPromo`
 *     mengklem ke Rp 0: kampanye "potongan Rp 50.000" bertarget merek membuat
 *     produk Rp 40.000 GRATIS, dan `pickCheapestPromo` justru memilihnya.
 *     Pagar yang sama sudah ada untuk override varian (`applyVariantOverride`,
 *     PROD 606 varian / 15 baris Rp 0). Nominal PERSIS sebesar harga tetap sah
 *     (Rp 0 yang disengaja), cermin aturan override.
 *   • persen > 100 — ditolak CHECK DB, tapi dijaga di sini juga.
 */
export function promoRuleUsable(
  rule: { mode: PromoDiscountMode; value: number } | null,
  base: number
): boolean {
  if (!rule) return false;
  if (!Number.isFinite(rule.value) || rule.value <= 0) return false;
  if (!Number.isFinite(base) || base < 0) return false;
  if (rule.mode === "persen") return rule.value <= 100;
  return rule.value <= base;
}

/**
 * Pemenang saat beberapa kampanye kena produk yang sama: YANG TERMURAH untuk
 * pembeli, dievaluasi pada `basePrice` — karena 'persen' dan 'nominal' baru bisa
 * dibandingkan setelah ada angka rupiah. Konsekuensi yang disengaja: pada produk
 * yang harga variannya berbeda, pemenangnya BOLEH berbeda antar varian.
 *
 * `finalPriceOf` (opsional) menerjemahkan harga promo ke HARGA AKHIR pembeli —
 * dibutuhkan sejak ada kampanye `stack`: harga promonya (90.000) bukan harga
 * yang dibayar reseller (63.000), dan "termurah" harus dinilai pada angka yang
 * benar-benar dibayar. pricing.ts mengisinya dengan tangga diskon level; tanpa
 * callback (pemanggil lama) harga promo dibandingkan apa adanya.
 *
 * Seri harga → `campaignId` terkecil. Tanpa tie-break stabil ini dua kampanye
 * kembar akan menang bergantian tergantung urutan baris dari DB — kelas bug yang
 * sudah dijaga di handlerBadge.ts (hash stabil) & sortReadyCandidates.
 */
export function pickCheapestPromo(
  promos: readonly ProductPromo[],
  basePrice: number,
  sizeLabel?: string | null,
  finalPriceOf?: (promoPrice: number, promo: ProductPromo) => number
): ProductPromo | null {
  let best: ProductPromo | null = null;
  let bestPrice = Number.POSITIVE_INFINITY;

  for (const promo of promos) {
    // Kampanye yang sengaja mengecualikan ukuran ini — atau yang aturannya tak
    // bisa dipakai pada harga ini (`promoRuleUsable`) — TIDAK ikut bersaing:
    // kalau ikut, ia bisa "menang" pada harga normal lalu melabeli varian itu
    // sebagai promo tanpa potongan apa pun, atau menang dengan harga Rp 0.
    if (!promoRuleUsable(promoDiscountForSize(promo, sizeLabel ?? null), basePrice)) continue;
    const promoPrice = applyPromo(basePrice, promo, sizeLabel);
    const price = finalPriceOf ? finalPriceOf(promoPrice, promo) : promoPrice;
    if (
      price < bestPrice ||
      (price === bestPrice && best !== null && promo.campaignId < best.campaignId)
    ) {
      best = promo;
      bestPrice = price;
    }
  }

  return best;
}

/**
 * Jumlah kartu per batch feed halaman Promo (cermin `HOME_FEED_BATCH`).
 *
 * Tinggal DI SINI, bukan di `promo-data.ts`, karena dibutuhkan komponen KLIEN
 * juga — dan `promo-data.ts` menarik `next/cache` lewat `catalog-cache.ts`,
 * sehingga mengimpornya dari klien akan menyeret kode server-only ke bundle.
 */
export const PROMO_FEED_BATCH = 12;

/**
 * Kampanye yang SEDANG berjalan pada `now`.
 *
 * Dipisah dari `promosForProduct` karena permukaan discovery (halaman `/promo`,
 * rail beranda) membutuhkan daftar KAMPANYE-nya sendiri — untuk kartu, chip
 * penyaring, dan hitung mundur — bukan sekadar potongan yang berlaku bagi satu
 * produk.
 */
export function runningPromoCampaigns(
  campaigns: readonly PromoCampaign[],
  now: Date
): PromoCampaign[] {
  return campaigns.filter((campaign) => isPromoRunning(campaign, now));
}

/**
 * Produk yang terkena SETIDAKNYA satu kampanye berjalan.
 *
 * SENGAJA lewat `promosForProduct`, bukan predikat baru: himpunan produk di
 * halaman Promo WAJIB persis sama dengan himpunan produk yang harganya dicoret
 * di kartu. Bila keduanya punya aturan sendiri, suatu hari halaman ini akan
 * memajang produk berharga normal (atau menyembunyikan yang sedang diskon)
 * tanpa satu pun error — kelas bug "dua salinan aturan bisnis" yang dihindari
 * repo ini di tempat lain (lihat catatan urutan penawaran di platformPricing).
 *
 * Generik `<T extends PromoProductRef>` supaya bisa dipakai atas index katalog
 * ringan (`HomeCatalogItem`) MAUPUN `Product` penuh — cermin helper di
 * homeCatalog.ts.
 */
export function promoProducts<T extends PromoProductRef>(
  products: readonly T[],
  campaigns: readonly PromoCampaign[],
  now: Date
): T[] {
  if (!campaigns.length) return [];
  return products.filter(
    (product) => promosForProduct(campaigns, product, now).length > 0
  );
}

/**
 * Sisa waktu kampanye untuk badge ("3 hari lagi"). `null` = tanpa batas waktu
 * ATAU sudah lewat — pemanggil tak merender apa pun.
 *
 * Dihitung dari SELISIH INSTAN, bukan dari komponen tanggal seperti
 * `daysUntilDeadline` (homeCatalog.ts): `ends_at` bertipe timestamptz sehingga
 * awalan ISO-nya adalah tanggal UTC, dan di WIB itu bisa meleset satu hari.
 * Dibulatkan ke ATAS supaya sisa waktu tak pernah tampak lebih pendek dari
 * kenyataannya — kesalahan ke arah "buru-buru" merugikan pembeli, bukan toko.
 * Kata-katanya mengikuti `deadlineChipLabel` agar dua badge waktu di kartu yang
 * sama tak berbicara dengan dua gaya berbeda.
 */
export function promoTimeLeftLabel(
  endsAt: string | null,
  now: Date
): string | null {
  const end = parseTime(endsAt);
  if (end === null) return null;
  const diff = end - now.getTime();
  if (diff <= 0) return null;
  const days = Math.ceil(diff / 86_400_000);
  return days <= 1 ? "Hari terakhir!" : `${days} hari lagi`;
}

/**
 * DUA PERTANYAAN BERBEDA tentang satu baris pesanan — dipisah di sini supaya
 * layar tak pernah menjawab yang satu dengan data milik yang lain.
 *
 *   A · `traced`  "baris ini DIBELI dengan harga kampanye X"
 *       Sumbernya JEJAK (`order_items.promo_campaign_name`, mig 3650). Inilah
 *       satu-satunya klaim yang sah di layar penagihan uang (keranjang & nota).
 *
 *   B · `running` "produk ini SEDANG kena kampanye X"
 *       Sumbernya keadaan HARI INI (`promosForProduct`). Berguna untuk admin
 *       yang sedang meninjau/membuat nota, tapi HARAM dipakai menjawab A: untuk
 *       pesanan lama ia berbohong dua arah — kampanye yang sudah berakhir
 *       membuat badge hilang padahal harganya memang harga promo, dan kampanye
 *       baru membuat badge muncul padahal harga baris itu harga normal lama.
 *
 * `traced` NULL BUKAN berarti "tanpa promo" — pesanan sebelum mig 3650, yang
 * dibuat admin, dan hasil impor semuanya tak ber-jejak. Karena itu fungsi ini
 * tak pernah memulangkan "tidak promo"; ia hanya menyatakan apa yang diketahui.
 *
 * `differs` = jejaknya ada TAPI kampanye yang berjalan sekarang bukan itu.
 * Sengaja dimunculkan, bukan disembunyikan: itu justru informasi yang dicari
 * admin ("harga di pesanan ini bukan harga promo yang sedang berjalan").
 */
export type PromoLineState = {
  traced: string | null;
  running: string[];
  differs: boolean;
};

export function promoLineState(
  tracedName: string | null | undefined,
  runningNames: readonly string[]
): PromoLineState {
  const traced = (tracedName ?? "").trim() || null;
  const running: string[] = [];
  for (const raw of runningNames) {
    const name = (raw ?? "").trim();
    if (!name) continue;
    if (!running.some((n) => n.toLowerCase() === name.toLowerCase())) {
      running.push(name);
    }
  }
  const differs =
    traced !== null &&
    running.length > 0 &&
    !running.some((n) => n.toLowerCase() === traced.toLowerCase());
  return { traced, running, differs };
}

/** Potongan versi singkat: "-30%" atau "-Rp 25.000". */
export function promoValueLabel(mode: PromoDiscountMode, value: number): string {
  const v = Number.isFinite(value) ? value : 0;
  return mode === "persen"
    ? `-${formatPromoValue(v)}%`
    : `-Rp ${new Intl.NumberFormat("id-ID").format(Math.round(v))}`;
}

/** Potongan versi singkat untuk chip/badge: "-30%" atau "-Rp 25.000". */
export function promoDiscountLabel(campaign: PromoCampaign): string {
  return promoValueLabel(campaign.discountMode, campaign.discountValue);
}

/**
 * Bahan penanda promo satu baris nota di editor ACC / Edit Nota — bentuk siap
 * render dari `promoLineState`, ditambah BESAR potongan kampanye yang sedang
 * berjalan.
 *
 * Nilainya ikut ditampilkan karena nama saja tak cukup di layar itu: kampanye
 * yang SAMA bisa berubah besarannya setelah pesanan dibuat, dan harga baris
 * tetap terkunci di angka lama ("harga terkunci saat pesan"). Terbukti di PROD
 * 2026-08-15 — nota INV2608150002 terkunci −40% sementara kampanye "Promo
 * Kemerdekaan" hari itu sudah −60%. `differs` TIDAK menangkap kasus itu (ia
 * membandingkan NAMA), jadi angkanya harus terbaca sendiri oleh admin.
 */
export function promoLineBadges(
  tracedName: string | null | undefined,
  promos: readonly ProductPromo[]
): { traced: string | null; running: string[]; differs: boolean } {
  const state = promoLineState(
    tracedName,
    promos.map((p) => p.name)
  );
  const labelByName = new Map<string, string>();
  for (const p of promos) {
    const name = (p.name ?? "").trim();
    if (!name || labelByName.has(name.toLowerCase())) continue;
    labelByName.set(name.toLowerCase(), `${name} ${promoValueLabel(p.mode, p.value)}`);
  }
  return {
    traced: state.traced,
    running: state.running.map((n) => labelByName.get(n.toLowerCase()) ?? n),
    differs: state.differs
  };
}

/** Label kebijakan level untuk layar admin & badge kampanye. */
export function promoLevelPolicyLabel(policy: PromoLevelPolicy): string {
  return policy === "stack" ? "Diskon harga coret" : "Harga promo final";
}

/**
 * Satu potongan sebagai teks badge: persen → "10%", nominal → "Rp 25.000"
 * (`compact` → "Rp25rb" untuk pil sempit di rail kartu). Tanpa tanda minus —
 * yang menggabungkan (`discountBadgeText`) menentukan bentuknya.
 */
export function discountCutLabel(
  cut: { mode: PromoDiscountMode; value: number },
  compact = false
): string {
  const v = Number.isFinite(cut.value) ? Math.max(0, cut.value) : 0;
  if (cut.mode === "persen") return `${formatPromoValue(v)}%`;
  const rp = Math.round(v);
  if (compact && rp >= 1000 && rp % 1000 === 0) {
    return `Rp${new Intl.NumberFormat("id-ID").format(rp / 1000)}rb`;
  }
  return `${compact ? "Rp" : "Rp "}${new Intl.NumberFormat("id-ID").format(rp)}`;
}

/**
 * Teks badge diskon — SATU pembuat untuk semua layar (kartu, halaman produk,
 * keranjang, pesanan, nota, WhatsApp, admin), supaya potongan yang sama tak
 * pernah dibaca dengan dua bahasa.
 *
 *   • dua potongan (promo harga coret + level) → "10% + 30%" — keputusan
 *     pemilik: promo dulu, urutan hitung; tanpa minus karena "+" sudah
 *     menyatakan tumpukan potongan;
 *   • satu potongan → "-10%" / "-Rp 25.000" (bentuk yang sudah dikenal pembeli);
 *   • `flame` memberi awalan "🔥 " bila ada potongan kampanye — penanda yang
 *     membedakan promo berbatas waktu dari harga level yang tetap.
 * `null` bila tak ada potongan sama sekali.
 */
export function discountBadgeText(
  cuts: readonly PriceCut[],
  options: { flame?: boolean; compact?: boolean } = {}
): string | null {
  const real = cuts.filter((c) => Number.isFinite(c.value) && c.value > 0);
  if (!real.length) return null;
  const flame = options.flame && real.some((c) => c.source === "promo") ? "🔥 " : "";
  if (real.length >= 2) {
    const joiner = options.compact ? "+" : " + ";
    return `${flame}${real.map((c) => discountCutLabel(c, options.compact)).join(joiner)}`;
  }
  return `${flame}-${discountCutLabel(real[0], options.compact)}`;
}

/** Ringkasan kampanye untuk kartu admin, mis. "Diskon 30% · semua produk". */
export function describePromo(campaign: PromoCampaign): string {
  const nominal = `Potongan Rp ${new Intl.NumberFormat("id-ID").format(
    Math.round(campaign.discountValue)
  )}/pcs`;
  const potonganDasar =
    campaign.discountMode === "persen"
      ? `Diskon ${formatPromoValue(campaign.discountValue)}%`
      : nominal;
  // Kampanye harga coret disebut di ringkasan (tampil di /promo & daftar admin)
  // supaya pembeli ber-level tahu diskon levelnya tetap berlaku di atasnya.
  const potongan =
    campaign.levelPolicy === "stack"
      ? `${potonganDasar} harga coret (+ diskon level)`
      : potonganDasar;

  const includes = campaign.targets.filter((t) => t.mode !== "exclude");
  const excludes = campaign.targets.filter((t) => t.mode === "exclude");
  const cakupanDasar =
    campaign.targetType === "all"
      ? "semua produk"
      : campaign.targetType === "product"
        ? `${includes.length} produk`
        : campaign.targetType === "brand"
          ? `${includes.length} merek`
          : `${includes.length} kategori`;
  // Pengecualian (mig 6010) disebut supaya "semua produk" tak dibaca harfiah.
  const cakupan = excludes.length
    ? `${cakupanDasar} kecuali ${excludes.length} ${excludes.every((t) => t.kind === "product") ? "produk" : "target"}`
    : cakupanDasar;

  const tipe =
    campaign.productTypeScope === "preorder"
      ? " (Pre-Order)"
      : campaign.productTypeScope === "ready_stock"
        ? " (Ready Stock)"
        : "";

  const kasir = campaign.applyToPos ? " · ikut Kasir" : "";

  // Kuota (mig 6020) disebut karena ia mengubah arti kampanye bagi admin:
  // "Diskon 30% · semua produk" yang berkuota 50 pcs bukan kampanye yang sama
  // dengan yang tanpa plafon. Angka SISA tak disebut di sini — ringkasan ini
  // dibuat dari baris kampanye saja, sedangkan sisa menuntut hitungan pesanan.
  const kuotaBagian: string[] = [];
  if (campaign.quotaTotal !== null) kuotaBagian.push(`${campaign.quotaTotal} pcs`);
  if (campaign.perCustomerLimit !== null) {
    kuotaBagian.push(`maks ${campaign.perCustomerLimit}/pembeli`);
  }
  const kuota = kuotaBagian.length ? ` · kuota ${kuotaBagian.join(", ")}` : "";

  return `${potongan} · ${cakupan}${tipe}${kasir}${kuota}`;
}

/** 30 → "30", 12.5 → "12,5" (tanpa nol di belakang koma). */
export function formatPromoValue(value: number): string {
  if (!Number.isFinite(value)) return "0";
  return Number.isInteger(value) ? String(value) : String(value).replace(".", ",");
}

export type PromoDraft = {
  id?: string | null;
  name: string;
  description?: string | null;
  isActive: boolean;
  startsAt?: string | null;
  endsAt?: string | null;
  discountMode: PromoDiscountMode;
  /** Mentah dari input form (boleh pakai koma desimal). */
  discountValue: string;
  productTypeScope: PromoProductTypeScope;
  targetType: PromoTargetType;
  targets: PromoTarget[];
  /** Baris isian potongan per ukuran (nilai mentah, cermin `discountValue`). */
  sizeRules: PromoSizeRuleDraft[];
  sizeRulesExclusive: boolean;
  /** Slot level yang berhak; kosong = semua pembeli. */
  levelSlots: number[];
  /** Cara bergabung dengan diskon level (seksi "Cara berlaku" di editor). */
  levelPolicy: PromoLevelPolicy;
  /** Berlaku juga di Kasir/POS (mig 6010). */
  applyToPos: boolean;
  /** Harga = min(promo, level) untuk kampanye `replace` (mig 6010). */
  neverWorse: boolean;
  /**
   * Kuota kampanye — mentah dari input form (mig 6020). KOSONG = tak terbatas.
   * Disimpan sebagai string, bukan `number | null`, supaya isian setengah jadi
   * ("1" saat admin sedang mengetik "10") tak dibaca sebagai plafon 1 pcs.
   */
  quotaTotal: string;
  /** Batas pcs per pembeli — mentah; kosong = tak dibatasi. */
  perCustomerLimit: string;
};

/** Satu baris isian ukuran di editor; `value` mentah dari input. */
export type PromoSizeRuleDraft = {
  size: string;
  mode: PromoDiscountMode;
  value: string;
};

/**
 * Angka dari input form, SADAR-MODE — mengikuti konvensi qrisFee.ts supaya dua
 * layar tak menafsirkan ketikan yang sama secara berbeda:
 *   • persen  → desimal diizinkan, koma dianggap titik ("12,5" = 12.5)
 *     (cermin `parsePercentInput`)
 *   • nominal → rupiah bulat, semua pemisah dibuang ("25.000" = 25000)
 *     (cermin `parseAmountInput`)
 * Kosong / tak terbaca → null.
 */
export function parsePromoValue(raw: string, mode: PromoDiscountMode): number | null {
  if (mode === "nominal") {
    const digits = (raw ?? "").replace(/[^0-9]/g, "");
    if (!digits) return null;
    const n = Number.parseInt(digits, 10);
    return Number.isFinite(n) ? n : null;
  }
  const t = (raw ?? "").trim().replace(",", ".");
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/**
 * Isian kuota (pcs) → bilangan bulat, atau null bila kosong = TAK TERBATAS.
 * Pemisah ribuan dibuang (cermin `parsePromoValue` mode nominal). "0" sengaja
 * dipulangkan sebagai 0, bukan null: "0 pcs" adalah salah ketik yang harus
 * ditolak `validatePromoDraft`, bukan diam-diam menjadi "tanpa kuota".
 */
export function parsePromoQuota(raw: string | null | undefined): number | null {
  const digits = (raw ?? "").replace(/[^0-9]/g, "");
  if (!digits) return null;
  const n = Number.parseInt(digits, 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * Validasi draf kampanye — cermin CHECK di mig 3470 supaya admin mendapat pesan
 * Indonesia alih-alih error Postgres mentah. `null` = lolos.
 */
export function validatePromoDraft(draft: PromoDraft): string | null {
  if (!draft.name.trim()) return "Nama kampanye wajib diisi.";

  const value = parsePromoValue(draft.discountValue, draft.discountMode);
  if (value === null) return "Besar diskon wajib diisi angka.";
  if (value <= 0) return "Besar diskon harus lebih dari 0.";
  if (draft.discountMode === "persen" && value > 100) {
    return "Diskon persen tidak boleh lebih dari 100%. Bila maksudnya rupiah, pilih mode Rp.";
  }

  const start = parseTime(draft.startsAt);
  const end = parseTime(draft.endsAt);
  if (start !== null && end !== null && end < start) {
    return "Tanggal berakhir tidak boleh lebih awal dari tanggal mulai.";
  }

  // Baris pengecualian (mig 6010) BUKAN target: "kecuali X" tanpa satu pun
  // target inklusif berarti kampanye tak mencakup apa pun.
  if (
    draft.targetType !== "all" &&
    !draft.targets.some((t) => t.mode !== "exclude" && t.kind === draft.targetType)
  ) {
    return "Pilih minimal satu target, atau ganti cakupan ke Semua Produk.";
  }

  // Slot level wajib bilangan bulat PROMO_GENERAL_SLOT..MAX_GROUP_SLOT (0 =
  // pelanggan umum, mig 5950) — REGRESI BERNAMA (bug live 2026-08-19): fetcher
  // grup yang membuang kolom `slot` membuat klik level di seksi 3b memasukkan
  // `undefined`; tombolnya tetap menyala, tapi normalisasi RPC membuang nilai
  // tak sah sehingga kampanye tersimpan diam-diam sebagai "semua pembeli".
  // Nilai tak sah harus MENGGAGALKAN simpan dengan pesan, bukan lolos senyap
  // sebagai cakupan yang berbeda dari yang dipilih admin.
  if (
    draft.levelSlots.some(
      (s) => !Number.isInteger(s) || s < PROMO_GENERAL_SLOT || s > MAX_GROUP_SLOT
    )
  ) {
    return "Pilihan level tidak valid — muat ulang halaman lalu pilih ulang levelnya.";
  }

  // Cermin CHECK `promo_campaigns_level_policy_check`: nilai di luar enum harus
  // GAGAL di sini dengan pesan Indonesia, bukan lolos ke RPC lalu ditolak DB.
  if (draft.levelPolicy !== "replace" && draft.levelPolicy !== "stack") {
    return "Cara berlaku promo tidak valid — muat ulang halaman lalu pilih ulang.";
  }

  // Kuota (mig 6020) — cermin CHECK `> 0` dan gerbang 22023 di RPC. Kosong =
  // tak terbatas dan SELALU sah; yang ditolak hanya angka yang mustahil.
  const quota = parsePromoQuota(draft.quotaTotal);
  const perCustomer = parsePromoQuota(draft.perCustomerLimit);
  if (quota !== null && quota <= 0) {
    return "Kuota kampanye harus lebih dari 0 — kosongkan bila tanpa batas.";
  }
  if (perCustomer !== null && perCustomer <= 0) {
    return "Batas per pembeli harus lebih dari 0 — kosongkan bila tanpa batas.";
  }
  if (quota !== null && perCustomer !== null && perCustomer > quota) {
    return `Batas per pembeli (${perCustomer} pcs) tidak boleh melebihi kuota kampanye (${quota} pcs).`;
  }

  return null;
}

/** Baris DB → kampanye. Koersi defensif (numeric bisa datang sebagai string). */
export function mapPromoRow(row: {
  id: string;
  store_id?: string | null;
  name?: string | null;
  description?: string | null;
  is_active?: boolean | null;
  starts_at?: string | null;
  ends_at?: string | null;
  discount_mode?: string | null;
  discount_value?: number | string | null;
  product_type_scope?: string | null;
  target_type?: string | null;
  size_rules_exclusive?: boolean | null;
  level_slots?: Array<number | string> | null;
  level_policy?: string | null;
  apply_to_pos?: boolean | null;
  never_worse?: boolean | null;
  quota_total?: number | string | null;
  per_customer_limit?: number | string | null;
  promo_campaign_targets?:
    | Array<{
        kind?: string | null;
        product_id?: string | null;
        name?: string | null;
        mode?: string | null;
      }>
    | null;
  promo_campaign_rules?:
    | Array<{
        axis?: string | null;
        match_value?: string | null;
        discount_mode?: string | null;
        discount_value?: number | string | null;
      }>
    | null;
}): PromoCampaign {
  const mode: PromoDiscountMode = row.discount_mode === "nominal" ? "nominal" : "persen";

  const scope: PromoProductTypeScope =
    row.product_type_scope === "preorder" || row.product_type_scope === "ready_stock"
      ? row.product_type_scope
      : "all";

  const targetType: PromoTargetType =
    row.target_type === "product" || row.target_type === "category" || row.target_type === "brand"
      ? row.target_type
      : "all";

  const targets: PromoTarget[] = (row.promo_campaign_targets ?? [])
    .map((t) => ({
      kind: (t.kind === "category" || t.kind === "brand" ? t.kind : "product") as PromoTargetKind,
      productId: t.product_id ?? null,
      name: t.name ?? "",
      // NULL / absen / nilai asing = include (baris pra-6010) — hanya 'exclude'
      // yang mengecualikan; menebak sebaliknya akan membuang produk dari promo.
      mode: (t.mode === "exclude" ? "exclude" : "include") as PromoTargetMode
    }))
    .filter((t) => (t.kind === "product" ? Boolean(t.productId) : Boolean(t.name.trim())));

  // Baris aturan yang labelnya kosong DIBUANG: aturan tanpa label mustahil
  // cocok dengan varian mana pun, jadi menyimpannya hanya melahirkan baris
  // hantu yang terlihat aktif di editor. Sumbu selain 'size' juga dilewati —
  // tabelnya sengaja disiapkan untuk sumbu lain (butir 2.2 usulan), dan versi
  // klien yang belum mengenalnya harus MENGABAIKAN, bukan salah menerapkan.
  const sizeRules: PromoSizeRule[] = (row.promo_campaign_rules ?? [])
    .filter((r) => (r.axis ?? "size") === "size")
    .map((r) => ({
      size: (r.match_value ?? "").trim(),
      mode: (r.discount_mode === "nominal" ? "nominal" : "persen") as PromoDiscountMode,
      value: num(r.discount_value)
    }))
    .filter((r) => Boolean(r.size));

  return {
    id: row.id,
    storeId: row.store_id ?? null,
    name: row.name ?? "",
    description: row.description ?? null,
    isActive: row.is_active !== false,
    startsAt: row.starts_at ?? null,
    endsAt: row.ends_at ?? null,
    discountMode: mode,
    discountValue: num(row.discount_value),
    productTypeScope: scope,
    targetType,
    targets,
    sizeRules,
    // Eksklusif hanya bermakna bila ada aturannya; tanpa aturan, `true` akan
    // membuat kampanye tak memberi potongan kepada siapa pun — kampanye yang
    // tampak aktif tapi tak pernah menurunkan satu harga pun.
    sizeRulesExclusive: sizeRules.length > 0 && row.size_rules_exclusive === true,
    // Nilai di luar PROMO_GENERAL_SLOT..MAX_GROUP_SLOT DIBUANG (bukan disimpan
    // apa adanya): slot yang tak ada penghuninya membuat kampanye tak cocok
    // dengan siapa pun, dan lebih baik ia jatuh ke "semua pembeli" daripada
    // senyap tak berlaku bagi siapa pun. 0 = pelanggan umum (mig 5950) SAH.
    //
    // ⚠️ BATASNYA WAJIB IKUT PLAFON DB. Penyaring ini berjalan saat MEMBACA, jadi
    // batas yang tertinggal di 4 akan membuat kampanye ber-`level_slots = {5}`
    // terbaca `[]` — dan `[]` berarti BERLAKU UNTUK SEMUA PEMBELI, bukan "tak
    // berlaku". Itu membocorkan diskon level ke pembeli yang tidak berhak, tanpa
    // satu pun error. Naikkan bersama `reseller_groups_slot_check` (mig 5600).
    levelSlots: (row.level_slots ?? [])
      .map((v) => Number(v))
      .filter((n) => Number.isInteger(n) && n >= PROMO_GENERAL_SLOT && n <= MAX_GROUP_SLOT),
    // Nilai tak dikenal / absen (select lama tanpa kolomnya) → `replace`: cara
    // berlaku yang lama, bukan menebak "stack" — menebak akan menurunkan harga
    // reseller dua kali tanpa ada yang memintanya.
    levelPolicy: row.level_policy === "stack" ? "stack" : "replace",
    // NULL (nullable, aturan 3840) / absen = false: kasir tak ikut & promo
    // menang mutlak — perilaku sebelum mig 6010.
    applyToPos: row.apply_to_pos === true,
    neverWorse: row.never_worse === true,
    // NULL / absen = TAK TERBATAS. Angka <= 0 juga dibaca "tak terbatas", bukan
    // "habis": CHECK DB melarangnya, jadi nilainya hanya bisa muncul dari baris
    // rusak — dan membaca baris rusak sebagai "kuota 0" akan mematikan kampanye
    // yang sebenarnya tak pernah dibatasi.
    quotaTotal: posIntOrNull(row.quota_total),
    perCustomerLimit: posIntOrNull(row.per_customer_limit)
  };
}

/** numeric/absen → bilangan bulat positif, selain itu null. */
function posIntOrNull(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === "") return null;
  const n = Math.trunc(Number(raw));
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Apakah produk ini sudah ditandai pada daftar target ber-peran `mode`?
 *
 * Dicocokkan lewat `productId` SAJA — nama hanyalah snapshot tampilan, dan dua
 * produk boleh bernama sama. Dipakai layar pemilih produk (centangnya) dan
 * penghitung "N dipilih".
 */
export function isProductTargeted(
  targets: readonly PromoTarget[],
  productId: string,
  mode: PromoTargetMode = "include"
): boolean {
  return targets.some(
    (t) => t.kind === "product" && t.productId === productId && (t.mode ?? "include") === mode
  );
}

/**
 * Tandai / lepas satu produk pada daftar target (tak memutasi input).
 *
 * Peran `mode` ikut dibawa supaya satu layar pemilih melayani daftar target
 * MAUPUN daftar "Kecuali" (mig 6010) tanpa dua salinan aturan. Baris berjenis
 * lain (kategori/merek) dan baris ber-peran lain TIDAK tersentuh: admin yang
 * membuka pemilih produk untuk daftar pengecualian tak boleh kehilangan target
 * inklusifnya.
 */
export function toggleProductTarget(
  targets: readonly PromoTarget[],
  product: { id: string; name: string },
  mode: PromoTargetMode = "include"
): PromoTarget[] {
  if (isProductTargeted(targets, product.id, mode)) {
    return targets.filter(
      (t) =>
        !(t.kind === "product" && t.productId === product.id && (t.mode ?? "include") === mode)
    );
  }
  return [...targets, { kind: "product", productId: product.id, name: product.name, mode }];
}

/**
 * Tandai BANYAK produk sekaligus ("pilih semua hasil cari") — idempoten, dan
 * nama produk yang sudah tertandai TIDAK ditimpa (snapshot nama di draf adalah
 * yang admin lihat saat menandainya).
 */
export function addProductTargets(
  targets: readonly PromoTarget[],
  products: ReadonlyArray<{ id: string; name: string }>,
  mode: PromoTargetMode = "include"
): PromoTarget[] {
  let out = [...targets];
  for (const p of products) {
    if (!isProductTargeted(out, p.id, mode)) {
      out = [...out, { kind: "product", productId: p.id, name: p.name, mode }];
    }
  }
  return out;
}

/** Lepas banyak produk sekaligus ("lepas semua hasil cari"). */
export function removeProductTargets(
  targets: readonly PromoTarget[],
  productIds: readonly string[],
  mode: PromoTargetMode = "include"
): PromoTarget[] {
  const buang = new Set(productIds);
  return targets.filter(
    (t) =>
      !(
        t.kind === "product" &&
        t.productId !== null &&
        buang.has(t.productId) &&
        (t.mode ?? "include") === mode
      )
  );
}

/** Draf form → argumen RPC `admin_upsert_promo_campaign`. */
/**
 * Baris isian ukuran → payload RPC. Baris yang labelnya kosong ATAU nilainya
 * tak terbaca DIBUANG: keduanya menghasilkan aturan yang tak bisa dijelaskan
 * ("ukuran ??? dapat potongan ???"), dan membiarkannya lolos berarti admin
 * mengira sudah mengatur sesuatu yang sebenarnya kosong.
 *
 * Nilai 0 SENGAJA dipertahankan — itu justru cara menyatakan "ukuran ini tidak
 * ikut promo" tanpa mengubah potongan utama.
 *
 * Label dinormalkan DI SINI juga, bukan hanya di trigger DB: form membaca
 * ulang draf yang sama untuk mendeteksi baris kembar, jadi kalau normalisasi
 * cuma hidup di server, dua isian "XXL" & "2XL" terlihat berbeda di layar lalu
 * saling menimpa senyap saat disimpan.
 */
export function buildSizeRulePayload(
  rules: readonly PromoSizeRuleDraft[]
): Array<{ size: string; mode: PromoDiscountMode; value: number }> {
  const out: Array<{ size: string; mode: PromoDiscountMode; value: number }> = [];
  const seen = new Set<string>();

  for (const raw of rules) {
    const size = normalizeSizeLabel(raw.size, false);
    if (!size) continue;
    const value = parsePromoValue(raw.value, raw.mode);
    if (value === null) continue;
    if (seen.has(size)) continue;
    seen.add(size);
    out.push({ size, mode: raw.mode, value });
  }

  return out;
}

/** Baris isian untuk satu ukuran, atau null bila admin belum mengisinya. */
export function sizeRuleFor(
  rules: readonly PromoSizeRuleDraft[],
  size: string
): PromoSizeRuleDraft | null {
  return rules.find((r) => r.size === size) ?? null;
}

/**
 * Setel nilai satu ukuran di draf (tak memutasi input).
 *
 * Nilai KOSONG membuang barisnya — itulah cara admin membatalkan aturan, dan
 * membiarkannya sebagai baris bernilai kosong akan tersimpan sebagai aturan
 * yang `buildSizeRulePayload` buang diam-diam, sehingga layar dan DB berbeda.
 * Nilai "0" TIDAK dibuang: itu pernyataan sah "ukuran ini tak ikut promo".
 */
export function setSizeRuleValue(
  rules: readonly PromoSizeRuleDraft[],
  size: string,
  value: string,
  fallbackMode: PromoDiscountMode
): PromoSizeRuleDraft[] {
  const rest = rules.filter((r) => r.size !== size);
  if (!value.trim()) return rest;
  const existing = rules.find((r) => r.size === size);
  return [...rest, { size, mode: existing?.mode ?? fallbackMode, value }];
}

/** Ganti mode satu ukuran; no-op bila ukurannya belum punya baris. */
export function setSizeRuleMode(
  rules: readonly PromoSizeRuleDraft[],
  size: string,
  mode: PromoDiscountMode
): PromoSizeRuleDraft[] {
  return rules.map((r) => (r.size === size ? { ...r, mode } : r));
}

export function promoDraftToPayload(draft: PromoDraft): {
  campaign: Record<string, unknown>;
  targets: Array<Record<string, unknown>>;
} {
  return {
    campaign: {
      id: draft.id ?? null,
      name: draft.name.trim(),
      description: draft.description?.trim() || null,
      isActive: draft.isActive,
      startsAt: draft.startsAt || null,
      endsAt: draft.endsAt || null,
      discountMode: draft.discountMode,
      discountValue: parsePromoValue(draft.discountValue, draft.discountMode) ?? 0,
      productTypeScope: draft.productTypeScope,
      targetType: draft.targetType,
      // Kunci ini SELALU dikirim oleh klien baru — RPC memperlakukan
      // "tidak ada kunci" sebagai "jangan sentuh aturan", jadi menghilangkannya
      // saat kosong akan membuat aturan mustahil dihapus lewat form.
      sizeRules: buildSizeRulePayload(draft.sizeRules),
      // Eksklusif tanpa aturan = kampanye yang tak menurunkan satu harga pun.
      sizeRulesExclusive: draft.sizeRulesExclusive && draft.sizeRules.length > 0,
      // SELALU dikirim: RPC memperlakukan "tidak ada kunci" sebagai "jangan
      // sentuh", jadi menghilangkannya saat kosong membuat cakupan mustahil
      // dikembalikan ke "semua pembeli" lewat form. Dinormalkan CERMIN RPC
      // (hanya bilangan bulat PROMO_GENERAL_SLOT..MAX_GROUP_SLOT, dedup, urut)
      // supaya payload yang terkirim = persis yang akan tersimpan — nilai tak
      // sah sudah ditolak lebih dulu oleh validatePromoDraft, ini lapis keduanya.
      // Batasnya WAJIB sama dengan penyaring di dalam
      // `admin_upsert_promo_campaign` (mig 5600 → 5950: `between 0 and 8`).
      levelSlots: [...new Set(draft.levelSlots)]
        .filter((s) => Number.isInteger(s) && s >= PROMO_GENERAL_SLOT && s <= MAX_GROUP_SLOT)
        .sort((a, b) => a - b),
      // SELALU dikirim (idiom yang sama): kunci absen = "jangan sentuh" di RPC,
      // jadi kampanye `stack` mustahil dikembalikan ke `replace` lewat form
      // kalau kuncinya hilang saat nilainya bawaan.
      levelPolicy: draft.levelPolicy === "stack" ? "stack" : "replace",
      // SELALU dikirim (idiom levelSlots): kunci absen = "jangan sentuh" di RPC.
      applyToPos: draft.applyToPos === true,
      neverWorse: draft.neverWorse === true,
      // SELALU dikirim; nilai null = HAPUS kuota (RPC membaca kunci-ADA sebagai
      // "setel", termasuk menyetel ke NULL). Tanpa ini, kampanye yang sudah
      // berkuota mustahil dikembalikan ke "tanpa batas" lewat form.
      quotaTotal: parsePromoQuota(draft.quotaTotal),
      perCustomerLimit: parsePromoQuota(draft.perCustomerLimit)
    },
    // Target inklusif hanya yang sejenis dengan cakupan (cakupan "Semua" = tanpa
    // target inklusif); PENGECUALIAN ikut apa pun cakupannya — "semua KECUALI"
    // justru kasus utamanya (mig 6010).
    targets: [
      ...(draft.targetType === "all"
        ? []
        : draft.targets.filter((t) => t.mode !== "exclude" && t.kind === draft.targetType)
      ).map((t) => ({ kind: t.kind, productId: t.productId, name: t.name, mode: "include" })),
      ...draft.targets
        .filter((t) => t.mode === "exclude")
        .map((t) => ({ kind: t.kind, productId: t.productId, name: t.name, mode: "exclude" }))
    ]
  };
}

/** Draf kosong untuk form "Buat Kampanye". */
export function emptyPromoDraft(): PromoDraft {
  return {
    id: null,
    name: "",
    description: "",
    isActive: true,
    startsAt: null,
    endsAt: null,
    discountMode: "persen",
    discountValue: "",
    productTypeScope: "all",
    targetType: "all",
    targets: [],
    sizeRules: [],
    sizeRulesExclusive: false,
    levelSlots: [],
    levelPolicy: "replace",
    applyToPos: false,
    // MENYALA untuk kampanye BARU (keputusan pemilik 2026-10-08, bersama diskon
    // merek per level mig 7070): pembeli membayar yang TERMURAH antara harga
    // promo & harga levelnya. Kampanye tersimpan tak tersentuh — `draftFromPromo`
    // membaca nilainya sendiri; admin tetap bisa mematikannya.
    neverWorse: true,
    quotaTotal: "",
    perCustomerLimit: ""
  };
}

/** Kampanye tersimpan → draf form (mode edit). */
export function draftFromPromo(campaign: PromoCampaign): PromoDraft {
  return {
    id: campaign.id,
    name: campaign.name,
    description: campaign.description ?? "",
    isActive: campaign.isActive,
    startsAt: campaign.startsAt,
    endsAt: campaign.endsAt,
    discountMode: campaign.discountMode,
    discountValue: formatPromoValue(campaign.discountValue),
    productTypeScope: campaign.productTypeScope,
    targetType: campaign.targetType,
    targets: campaign.targets,
    sizeRules: campaign.sizeRules.map((r) => ({
      size: r.size,
      mode: r.mode,
      value: formatPromoValue(r.value)
    })),
    sizeRulesExclusive: campaign.sizeRulesExclusive,
    levelSlots: campaign.levelSlots,
    levelPolicy: campaign.levelPolicy,
    applyToPos: campaign.applyToPos,
    neverWorse: campaign.neverWorse,
    // null (tak terbatas) → isian KOSONG, bukan "0": "0" akan gagal validasi
    // dan membuat kampanye tanpa kuota mustahil disimpan ulang tanpa diedit.
    quotaTotal: campaign.quotaTotal === null ? "" : String(campaign.quotaTotal),
    perCustomerLimit:
      campaign.perCustomerLimit === null ? "" : String(campaign.perCustomerLimit)
  };
}

/**
 * Isi rail "Sedang Promo" beranda — LINTAS TIPE, tipe tab aktif didahulukan.
 *
 * Keputusan pemilik (2026-08): rail ini WAJIB tampil di KEDUA tab, Pre-Order
 * maupun Ready Stock. Sebelumnya isinya disaring `productsByType` lebih dulu,
 * sehingga kampanye yang cakupannya satu tipe saja (`product_type_scope`)
 * membuat rail-nya LENYAP TOTAL di tab seberang — promo ready stock tak pernah
 * terlihat oleh pembeli yang sedang membuka tab Pre-Order, padahal keduanya
 * satu etalase dan satu-satunya jalan menemukannya adalah menebak untuk
 * berpindah tab.
 *
 * URUTAN DI SINI BUKAN HIASAN: rail dipotong `limit`, jadi tanpa mendahulukan
 * tipe tab aktif sebuah tab bisa kehabisan slot sebelum sampai ke produk promo
 * MILIKNYA SENDIRI (mis. 30 promo ready stock + 3 promo preorder, cap 10 → tab
 * Pre-Order tak menampilkan satu pun promo preorder — persis kebalikan dari
 * yang diminta). Partisinya STABIL: urutan asal di dalam tiap kelompok
 * dipertahankan, jadi isi rail tetap bisa ditebak dan tidak berkocok sendiri.
 *
 * Himpunan produknya tetap lewat `promoProducts` — lihat alasan "dua salinan
 * aturan bisnis" di docstring fungsi itu.
 */
export function promoRailProducts<T extends PromoProductRef>(
  products: readonly T[],
  campaigns: readonly PromoCampaign[],
  now: Date,
  activeType: "preorder" | "ready_stock",
  limit: number
): T[] {
  if (limit <= 0) return [];
  const active: T[] = [];
  const rest: T[] = [];
  for (const product of promoProducts(products, campaigns, now)) {
    // `productType` kosong dianggap 'preorder' — cermin `effectiveType`
    // (homeCatalog.ts) & `campaignCoversProduct` di berkas ini.
    const type =
      product.productType === "ready_stock" ? "ready_stock" : "preorder";
    (type === activeType ? active : rest).push(product);
  }
  return active.concat(rest).slice(0, limit);
}
